import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  TextField,
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  Grid,
  Typography,
  Autocomplete,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useEffect, useState } from "react";
import {
  useLazyGetPortfolioByIdQuery,
  useUpdatePortfolioMutation,
} from "../../services/users";
import { showError, showToast } from "../../constants/toasts";
import { UploadMedia } from "../../utils/mediaUpload";
import useTranslation from "../../hooks/traslation";
import { SKILLS, INDUSTRY, MEDIA_TYPE } from "../../utils/enums";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface MediaItem {
  _id: string;
  link: string;
  mediaType: number;
}

interface PortfolioData {
  _id: string;
  industry: number[];
  skills: number[];
  coverImage: string;
  title: string;
  duration: string;
  media: MediaItem[];
  intro: string;
}

const EditPortfolio = () => {
  const navigate = useNavigate();
  const { id, ProjectId } = useParams();
  const [getPortfolioById] = useLazyGetPortfolioByIdQuery();
  const [updatePortfolio] = useUpdatePortfolioMutation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PortfolioData | null>(null);
  const [coverImage, setCoverImage] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<number[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<number[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaItem[]>([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: data?.title || "",
      duration: data?.duration || "",
      intro: data?.intro || "",
      industry: selectedIndustry,
      skills: selectedSkills,
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required("Title is required")
        .max(40, "Title must be at most 40 characters")
        .matches(/^[^0-9]*$/, "Title cannot contain numbers"),
      duration: Yup.string()
        .required("Duration is required")
        .matches(/^\d{1,2}$/, "Duration must be 1-2 digits"),
      intro: Yup.string()
        .required("Introduction is required")
        .max(500, "Introduction must be at most 500 characters"),
      industry: Yup.array().min(1, "Please select at least one industry"),
      skills: Yup.array().min(1, "Please select at least one skill"),
    }),
    onSubmit: async (values) => {
      try {
        const body = {
          title: values.title,
          duration: values.duration,
          intro: values.intro,
          coverImage,
          industry: values.industry,
          skills: values.skills,
          media: mediaFiles.map((media) => ({
            link: media.link,
            mediaType: media.mediaType,
          })),
        };

        const response = await updatePortfolio({
          id: ProjectId || "",
          body,
        }).unwrap();
        if (response?.statusCode === 200) {
          showToast("Portfolio updated successfully");
          // const freelancerId = response.data?.freelancerId || "";
          navigate(`/manage-freelancer/detail/${id}?page=1`);
        }
      } catch (error: any) {
        showError(error?.data?.message || "Failed to update portfolio");
      }
    },
  });

  const handleCoverImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files?.length) return;

    const file = files[0];
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      showError("Please upload an image in JPG, JPEG or PNG format");
      return;
    }

    try {
      const response = await UploadMedia(file);
      if (response?.data?.image) {
        setCoverImage(response.data.image);
      }
    } catch (error) {
      showError("Failed to upload cover image");
    }
  };

  const handleMediaUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const files = event.target.files;
    if (!files?.length) return;

    const file = files[0];
    let mediaType = MEDIA_TYPE.IMAGE;

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showError("File size should not exceed 5MB");
      event.target.value = "";
      return;
    }

    // Check total number of media files
    if (mediaFiles.length >= 10) {
      showError("Maximum 10 media files are allowed");
      event.target.value = "";
      return;
    }

   
    if (file.type.startsWith("video/")) {
      mediaType = MEDIA_TYPE.VIDEO;
    } else if (!file.type.startsWith("image/")) {
      showError("Please upload an image or video file");
      return;
    }

    try {
      const response = await UploadMedia(file);
      if (response?.data) {
        const newMedia = {
          _id: response.data._id || "",
          link: response.data.image || response.data.link || "",
          mediaType: mediaType,
        };

        setMediaFiles((prev) => {
          const updated = [...prev];
          updated[index] = newMedia;
          return updated;
        });
      }
      event.target.value = "";
    } catch (error) {
      showError("Failed to upload media file");
      event.target.value = "";
    }
  };

  const fetchPortfolioData = async () => {
    if (!ProjectId) return;
    setLoading(true);
    try {
      const response = await getPortfolioById({ ProjectId }).unwrap();
      if (response?.statusCode === 200) {
        const portfolioData = response.data;
        setData(portfolioData);
        setCoverImage(portfolioData.coverImage);
        setSelectedSkills(portfolioData.skills);
        setSelectedIndustry(portfolioData.industry);
        setMediaFiles(portfolioData.media);
        formik.setValues({
          title: portfolioData.title,
          duration: portfolioData.duration,
          intro: portfolioData.intro,
          industry: portfolioData.industry,
          skills: portfolioData.skills,
        });
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch portfolio data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolioData();
  }, [ProjectId]);

  const industryOptions = Object.entries(INDUSTRY)
    .filter(([key]) => !isNaN(Number(key)))
    .map(([key, value]) => ({
      _id: Number(key),
      name: value,
    }));

  const skillsOptions = Object.entries(SKILLS)
    .filter(([key]) => !isNaN(Number(key)))
    .map(([key, value]) => ({
      _id: Number(key),
      name: value,
    }));

  const renderMediaPreview = (media: MediaItem) => {
    if (media.mediaType === MEDIA_TYPE.VIDEO) {
      return (
        <div className="upload_image no_radius v2">
          <div
            className="upload_image_holder"
            style={{
              aspectRatio: "1",
              position: "relative",
              maxWidth: "200px",
              maxHeight: "200px",
              margin: "0 auto",
            }}
          >
            <video
              controls
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <source src={media.link} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      );
    }

    if (media.mediaType === MEDIA_TYPE.PDF) {
      return (
        <div className="upload_image no_radius v2">
          <div
            className="upload_image_holder"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <img
              src="/static/images/pdf-icon.png"
              alt="PDF"
              style={{ width: "64px", height: "64px", marginBottom: "10px" }}
            />
            <Typography variant="body2" style={{ wordBreak: "break-all" }}>
              PDF Document
            </Typography>
            <Button
              variant="contained"
              size="small"
              href={media.link}
              target="_blank"
              style={{ marginTop: "10px" }}
            >
              View PDF
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="upload_image no_radius v2">
        <div className="upload_image_holder">
          <figure>
            <CardMedia component="img" image={media.link} alt="media" />
          </figure>
        </div>
      </div>
    );
  };

  const handleDeleteMedia = (index: number) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Edit Portfolio</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-freelancer")}>
            Manage Freelancer
          </span>{" "}
          - Edit Portfolio
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Cover Image</label>
              <div className="docs_div v2">
                <Grid container spacing={2}>
                  {coverImage ? (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <div
                        className="doc_preview_container"
                        style={{ position: "relative" }}
                      >
                        <div className="upload_image no_radius v2">
                          <div className="upload_image_holder">
                            <figure>
                              <CardMedia
                                component="img"
                                image={coverImage}
                                alt="cover"
                              />
                            </figure>
                          </div>
                        </div>
                        <Tooltip title="Edit">
                          <label
                            className="upload_image no_radius v2"
                            htmlFor="cover-image-upload"
                            style={{
                              position: "absolute",
                              bottom: 8,
                              right: 8,
                              width: "32px",
                              height: "32px",
                              minWidth: "32px",
                              padding: 0,
                              borderRadius: "50%",
                              backgroundColor: "rgba(255,255,255,0.9)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          >
                            <Input
                              sx={{ display: "none" }}
                              id="cover-image-upload"
                              type="file"
                              inputProps={{
                                accept: "image/png,image/jpeg,image/jpg",
                              }}
                              onChange={handleCoverImageUpload}
                            />
                            <span className="upload_image_holder">
                              <figure
                                className="add_img"
                                style={{
                                  margin: 0,
                                  width: "20px",
                                  height: "20px",
                                }}
                              >
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt="icon"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              </figure>
                            </span>
                          </label>
                        </Tooltip>
                      </div>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="cover-image-upload"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="cover-image-upload"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg,image/jpg",
                          }}
                          onChange={handleCoverImageUpload}
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    </Grid>
                  )}
                </Grid>
              </div>
            </div>

            <div className="control_group w_50">
              <label>Title</label>
              <TextField
                placeholder="Enter title"
                variant="outlined"
                className="text_field"
                fullWidth
                hiddenLabel
                name="title"
                value={formik.values.title}
                onChange={(e) => {
                  const value = e.target.value.replace(/[0-9]/g, "");
                  if (value.length <= 40) {
                    formik.setFieldValue("title", value);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData("text");
                  const noNumbers = pastedText.replace(/[0-9]/g, "");
                  if (noNumbers.length <= 40) {
                    formik.setFieldValue("title", noNumbers);
                  }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group w_50">
              <label>Duration</label>
              <TextField
                placeholder="Enter duration"
                variant="outlined"
                className="text_field"
                fullWidth
                hiddenLabel
                name="duration"
                value={formik.values.duration}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 2) {
                    formik.setFieldValue("duration", value);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData("text");
                  const numbersOnly = pastedText.replace(/\D/g, "");
                  if (numbersOnly.length <= 2) {
                    formik.setFieldValue("duration", numbersOnly);
                  }
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.duration && Boolean(formik.errors.duration)
                }
                helperText={formik.touched.duration && formik.errors.duration}
                inputProps={{ maxLength: 2 }}
              />
            </div>

            <div className="control_group w_50">
              <label>Industry</label>
              <Autocomplete
                className="users_autocomplete"
                multiple
                options={industryOptions}
                disableCloseOnSelect
                getOptionLabel={(option) =>
                  typeof option?.name === "string" ? option.name : ""
                }
                value={industryOptions.filter((s) =>
                  selectedIndustry.includes(s._id)
                )}
                onChange={(event, newValue) => {
                  const selectedIds = newValue.map((item) => item._id);
                  setSelectedIndustry(selectedIds);
                }}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Industry"
                    fullWidth
                    error={
                      formik.touched.industry && Boolean(formik.errors.industry)
                    }
                    helperText={
                      formik.touched.industry && formik.errors.industry
                    }
                    FormHelperTextProps={{
                      style: {
                        color: "#ff0000",
                        fontSize: "12px",
                        marginTop: "4px",
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option._id}
                    />
                  ))
                }
                clearOnBlur={false}
                clearIcon
              />
            </div>

            <div className="control_group w_50">
              <label>Skills</label>
              <Autocomplete
                className="users_autocomplete"
                multiple
                options={skillsOptions}
                disableCloseOnSelect
                getOptionLabel={(option) =>
                  typeof option?.name === "string" ? option.name : ""
                }
                value={skillsOptions.filter((s) =>
                  selectedSkills.includes(s._id)
                )}
                onChange={(event, newValue) => {
                  const selectedIds = newValue.map((item) => item._id);
                  setSelectedSkills(selectedIds);
                }}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Skills"
                    fullWidth
                    error={
                      formik.touched.skills && Boolean(formik.errors.skills)
                    }
                    helperText={formik.touched.skills && formik.errors.skills}
                    FormHelperTextProps={{
                      style: {
                        color: "#ff0000",
                        fontSize: "12px",
                        marginTop: "4px",
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option._id}
                    />
                  ))
                }
                clearOnBlur={false}
                clearIcon
              />
            </div>

            <div className="control_group w_100">
              <label>Introduction</label>
              <TextField
                placeholder="Enter introduction"
                variant="outlined"
                className="text_field"
                fullWidth
                multiline
                rows={4}
                name="intro"
                value={formik.values.intro}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    formik.handleChange(e);
                  }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.intro && Boolean(formik.errors.intro)}
                helperText={formik.touched.intro && formik.errors.intro}
                inputProps={{ maxLength: 500 }}
              />
            </div>

            <div className="control_group w_100">
              <label>Media Files</label>
              <div className="docs_div v2">
                <Grid container spacing={2}>
                  {mediaFiles.map((media, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={media._id || index}
                    >
                      <div
                        className="doc_preview_container"
                        style={{ position: "relative" }}
                      >
                        <div className="upload_image no_radius v2">
                          <div className="upload_image_holder">
                            {renderMediaPreview(media)}
                          </div>
                        </div>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDeleteMedia(index)}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              backgroundColor: "rgba(255,255,255,0.9)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,1)",
                              },
                              width: "32px",
                              height: "32px",
                              padding: 0,
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <label
                            className="upload_image no_radius v2"
                            htmlFor={`media-upload-${index}`}
                            style={{
                              position: "absolute",
                              bottom: 8,
                              right: 8,
                              width: "32px",
                              height: "32px",
                              minWidth: "32px",
                              padding: 0,
                              borderRadius: "50%",
                              backgroundColor: "rgba(255,255,255,0.9)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          >
                            <Input
                              sx={{ display: "none" }}
                              id={`media-upload-${index}`}
                              type="file"
                              inputProps={{ accept: "video/*,image/*" }}
                              onChange={(event: any) =>
                                handleMediaUpload(event, index)
                              }
                            />
                            <span className="upload_image_holder">
                              <figure
                                className="add_img"
                                style={{
                                  margin: 0,
                                  width: "20px",
                                  height: "20px",
                                }}
                              >
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt="icon"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              </figure>
                            </span>
                          </label>
                        </Tooltip>
                      </div>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Tooltip title="Upload media files (Max 10 files, 5MB each)">
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="new-media-upload"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="new-media-upload"
                          type="file"
                          inputProps={{ accept: "video/*,image/*" }}
                          onChange={(event: any) =>
                            handleMediaUpload(event, mediaFiles.length)
                          }
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    </Tooltip>
                  </Grid>
                </Grid>
              </div>
            </div>
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Portfolio"}
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default EditPortfolio;
